import React from 'react';
import { Character } from '../../types';
import { KeywordBadge } from '../ui/KeywordBadge';
import { DamageTypeBadge } from '../ui/DamageTypeBadge';

interface CharacterDetailProps {
  character: Character | null;
}

const CharacterDetail: React.FC<CharacterDetailProps> = ({ character }) => {
  if (!character) {
    return (
      <div className="character-detail-panel">
        <div className="character-detail-empty">
          <div className="empty-icon">📖</div>
          <h2>Select a Character</h2>
          <p>Choose a character from the list to view detailed information</p>
        </div>
      </div>
    );
  }

  // Format range for display
  const formatRange = (range: { min: number; max: number; text?: string }) => {
    if (range.text) {
      return range.text;
    }
    if (range.min === range.max) {
      return `${range.min} pace${range.min !== 1 ? 's' : ''}`;
    }
    return `${range.min}-${range.max} paces`;
  };

  const capitalizeFirstLetter = (val: string) => {
    return val.charAt(0).toUpperCase() + val.slice(1);
  }

  const formatUseLabel = (maxUses: number) => {
    if (maxUses === 1) {
      return "use";
    }
    else {
      return "uses";
    }
  }

  const formatStat = (val: number) => {
    if (val === 999) {
      return "∞";
    }
    else {
      return val;
    }
  }

  const mergedKeywords = (types: string[], keywords: any[]) => {
    // Convert types (strings) to keyword objects
    const typesAsKeywords = types.map(type => ({ name: type, value: undefined }));
    // Merge both arrays
    const merged = [...typesAsKeywords, ...keywords];
    // De-duplicate by name, keeping the first occurrence (which preserves types first, then keywords)
    const seen = new Set<string>();
    return merged.filter(item => {
      if (seen.has(item.name)) {
        return false;
      }
      seen.add(item.name);
      return true;
    });
  }

  // Format effects for cleaner display
  // const formatEffects = (effects: any[]) => {
  //   return effects
  //     .filter(effect => {
  //       // Filter out the primary damage effect if it's just restating the damage
  //       if (effect.statType === 'health' && effect.operation === 'add' && effect.modifier < 0) {
  //         return false;
  //       }
  //       return true;
  //     })
  //     .map(effect => {
  //       const parts = [];
        
  //       // Handle special effects
  //       if (effect.specialEffects && effect.specialEffects.length > 0) {
  //         parts.push(...effect.specialEffects.map((e: string) => {
  //           // Format special effect names nicely
  //           return e.replace(/([A-Z])/g, ' $1').trim().toLowerCase();
  //         }));
  //       }
        
  //       // Handle push distance
  //       if (effect.pushDistance) {
  //         parts.push(`push ${effect.pushDistance} pace${effect.pushDistance !== 1 ? 's' : ''}`);
  //       }
        
  //       // Handle stat modifications (but not primary damage)
  //       if (effect.statType !== 'health' || effect.modifier > 0) {
  //         let statChange = '';
  //         if (effect.operation === 'add') {
  //           statChange = `${effect.modifier > 0 ? '+' : ''}${effect.modifier} ${effect.statType}`;
  //         } else if (effect.operation === 'subtract') {
  //           statChange = `-${Math.abs(effect.modifier)} ${effect.statType}`;
  //         } else if (effect.operation === 'multiply') {
  //           statChange = `×${effect.modifier} ${effect.statType}`;
  //         } else if (effect.operation === 'set') {
  //           statChange = `set ${effect.statType} to ${effect.modifier}`;
  //         }
  //         if (statChange) {
  //           parts.push(statChange);
  //         }
  //       }
        
  //       return parts;
  //     })
  //     .flat()
  //     .filter(Boolean);
  // };

  return (
    <div className="character-detail-panel">
      <div className="character-detail-scroll">
        {/* Header */}
        <div className="character-detail-header">
          <div>
            <h1>{character.name}</h1>
          </div>
        </div>

        {/* Base Stats */}
        <div className="detail-section">
          <div className="stats-grid-detail">
            <div className="stat-card">
              <div className="stat-icon">❤️</div>
              <div className="stat-info">
                <div className="stat-label">Health</div>
                <div className="stat-value">{formatStat(character.baseStats.health)}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✨</div>
              <div className="stat-info">
                <div className="stat-label">Will</div>
                <div className="stat-value">{formatStat(character.baseStats.will)}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⚔️</div>
              <div className="stat-info">
                <div className="stat-label">Attack</div>
                <div className="stat-value">+{formatStat(character.baseStats.attackBonus)}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🛡️</div>
              <div className="stat-info">
                <div className="stat-label">Defense</div>
                <div className="stat-value">+{formatStat(character.baseStats.defenseBonus)}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⚡</div>
              <div className="stat-info">
                <div className="stat-label">Speed</div>
                <div className="stat-value">{formatStat(character.baseStats.speed)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Character Description */}
        {character.description && (
          <div className="detail-section">
            <p className="character-description">{character.description}</p>
          </div>
        )}

        {/* Keywords */}
        {character.keywords.length > 0 && (
          <div className="detail-section">
            <h2>Keywords & Traits</h2>
            <div className="keywords-grid">
              {mergedKeywords(character.types, character.keywords).map((keyword, index) => (
                <div key={index} className="keyword-badge">
                  <KeywordBadge
                    keyword={keyword.name}
                    value={keyword.value}
                    showTooltip={true}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Immunities */}
        {character.immunities && character.immunities.length > 0 && (
          <div className="detail-section">
            <h2>Immunities</h2>
            <div className="immunities-list">
              {character.immunities.map((immunity, index) => (
                <span key={index} className="immunity-badge">
                  🛡️ {capitalizeFirstLetter(immunity)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Abilities */}
        <div className="detail-section">
          <h2>Abilities ({character.abilities.length})</h2>
          <div className="abilities-detail-list">
            {character.abilities.map((ability, index) => (
              <div key={index} className="ability-detail-card">
                <div className="ability-detail-header">
                  <h3>{ability.requiresAttackRoll && '🎲 '}{ability.name}</h3>
                  <div className="ability-detail-stats">
                    {ability.damage > 0 && (
                      <span className="ability-stat damage">
                        ⚔️ {ability.damage} dmg
                      </span>
                    )}
                    {ability.damageType && (
                      <DamageTypeBadge
                        damageType={ability.damageType}
                        showTooltip={true}
                        className="ability-stat"
                      />
                    )}
                    <span className="ability-stat range">
                      📏 {formatRange(ability.range)}
                    </span>
                    {ability.maxUses !== undefined && ability.maxUses !== null && (
                      <span className="ability-stat max-uses">
                        🔄 {ability.maxUses} {formatUseLabel(ability.maxUses)}
                      </span>
                    )}
                    {ability.isWillAttack && (
                      <span className="ability-stat will-attack">
                        ✨ Will Attack
                      </span>
                    )}
                  </div>
                </div>

                {/* Ability Keywords - directly under name */}
                {ability.keywords && ability.keywords.length > 0 && (
                  <div className="ability-keywords">
                    <div className="keywords-grid">
                      {ability.keywords.map((keyword, keywordIndex) => (
                        <div key={keywordIndex} className="keyword-badge ability-keyword-badge">
                          <KeywordBadge
                            keyword={keyword}
                            showTooltip={true}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="ability-description">{ability.description}</p>

                {/* Effects */}
                {/* {ability.effects && ability.effects.length > 0 && (() => {
                  const formattedEffects = formatEffects(ability.effects);
                  return formattedEffects.length > 0 ? (
                    <div className="ability-effects">
                      <strong>Effects:</strong>
                      <ul>
                        {formattedEffects.map((effect, effectIndex) => (
                          <li key={effectIndex}>{effect}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null;
                })()} */}

                {/* Target Rules */}
                {/* <div className="ability-targets">
                  <strong>Valid Targets:</strong>
                  {ability.validTargets && ability.validTargets.length > 0 ? (
                    <ul>
                      {ability.validTargets.map((target, targetIndex) => {
                        const parts = [];
                        if (target.characterType) {
                          parts.push(`Type: ${target.characterType}`);
                        }
                        if (target.excludeSelf) {
                          parts.push('Exclude Self');
                        }
                        if (target.maxTargets) {
                          parts.push(`Max Targets: ${target.maxTargets}`);
                        }
                        return (
                          <li key={targetIndex}>
                            {parts.length > 0 ? parts.join(' - ') : 'All'}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <ul>
                      <li>All</li>
                    </ul>
                  )}
                </div> */}

                {/* Sub-abilities */}
                {ability.subAbilities && ability.subAbilities.length > 0 && (
                  <div className="sub-abilities-detail">
                    <h4>Sub-Abilities:</h4>
                    {ability.subAbilities.map((subAbility, subIndex) => (
                      <div key={subIndex} className="sub-ability-detail-card">
                        <div className="sub-ability-detail-header">
                          <strong>{subAbility.name}</strong>
                          <div className="sub-ability-stats">
                            {subAbility.damage > 0 && (
                              <span className="sub-ability-stat damage">⚔️ {subAbility.damage}</span>
                            )}
                            {subAbility.damageType && (
                              <DamageTypeBadge
                                damageType={subAbility.damageType}
                                showTooltip={true}
                                className="sub-ability-stat"
                              />
                            )}
                            <span className="sub-ability-stat range">📏 {formatRange(subAbility.range)}</span>
                            {subAbility.maxUses !== undefined && subAbility.maxUses !== null && (
                              <span className="sub-ability-max-uses">🔄 {subAbility.maxUses}</span>
                            )}
                            {subAbility.isWillAttack && (
                              <span className="will-badge">✨ Will</span>
                            )}
                          </div>
                        </div>
                        
                        {/* Sub-ability Keywords - directly under name */}
                        {subAbility.keywords && subAbility.keywords.length > 0 && (
                          <div className="ability-keywords">
                            <div className="keywords-grid">
                              {subAbility.keywords.map((keyword, keywordIndex) => (
                                <div key={keywordIndex} className="keyword-badge ability-keyword-badge">
                                  <KeywordBadge
                                    keyword={keyword}
                                    showTooltip={true}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <p className="sub-ability-description">{subAbility.description}</p>
                        
                        {/* {subAbility.effects && subAbility.effects.length > 0 && (() => {
                          const formattedEffects = formatEffects(subAbility.effects);
                          return formattedEffects.length > 0 ? (
                            <div className="sub-ability-effects">
                              <strong>Effects:</strong>
                              <ul>
                                {formattedEffects.map((effect, effectIndex) => (
                                  <li key={effectIndex}>{effect}</li>
                                ))}
                              </ul>
                            </div>
                          ) : null;
                        })()} */}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CharacterDetail;
